/**
 * Organization themes (#845, docs/plans/2026-10-02-conversation-themes.md §2).
 *
 * The closed state sets of the two D1 tables created by migration
 * 202610082305_org_themes.sql. The same lists are CHECK constraints in SQL, so
 * a value added here without a table rebuild is rejected by the database.
 * Below them — the request/response contracts of the theme-set routes
 * `/api/v1/analytics/voice/themes` (PR 2, spec §2): code pattern, name and
 * definition lengths, the cap.
 *
 * - `reserved`: the code is taken by an accepted proposal, the theme is not yet
 *   in the prompt or the analytics
 * - `active`: in the prompt and the analytics
 * - `archived`: out of both, the code and `norm_key` stay taken
 */
import { z } from 'zod/v4';
import { lockedDemoOnlySchema } from './common';
import {
  RESERVED_THEME_CODE,
  THEME_CODE_PATTERN,
  normalizeThemeKey,
} from '@aica/core/voc/theme-key';
import type { ThemeOverlapDimension } from '@aica/core/voc/theme-overlap';

export const orgThemeStatusSchema = z.enum(['reserved', 'active', 'archived']);

export const ORG_THEME_STATUSES = orgThemeStatusSchema.options;

export type OrgThemeStatus = z.infer<typeof orgThemeStatusSchema>;

/** Starter proposal attempt (`org_theme_bootstraps.status`, spec §2 Р1). */
export const orgThemeBootstrapStatusSchema = z.enum(['running', 'ready', 'failed']);

export const ORG_THEME_BOOTSTRAP_STATUSES = orgThemeBootstrapStatusSchema.options;

export type OrgThemeBootstrapStatus = z.infer<typeof orgThemeBootstrapStatusSchema>;

// ============================================================================
// Theme-set routes (PR 2, spec §2, §2.1)
// ============================================================================

/**
 * Themes in `active` and `reserved` together (spec §2). The archive is not
 * counted. The database holds it in the one statement that enters a counted
 * status (`OrgThemesRepository`), never in a read-then-write.
 */
export const ORG_THEME_CAP = 30;

/** `org_themes.name` — the migration has only the lower bound (CHECK). */
export const ORG_THEME_NAME_MAX = 60;

/** `org_themes.definition` — may be empty (spec §4: candidate without one). */
export const ORG_THEME_DEFINITION_MAX = 80;

/**
 * Plain single-line text only (`LINE_BREAKING_CHAR_CLASS` is the one class —
 * the step's prompt and its `suggested_theme` cleanup derive from it too): the name and definition go into a prompt
 * (PR 3) and onto the screen — and so does the step's `suggested_theme`
 * (`voice-extraction.ts`), a theme name the model proposes. Refused: control characters (`Cc` — newline,
 * tab), format characters (`Cf` — zero-width, bidi overrides such as U+202E
 * that would show one text and send another) and the Unicode line and
 * paragraph separators (`Zl`, `Zp`, U+2028/U+2029 — line breaks by another name).
 */
export const LINE_BREAKING_CHAR_CLASS = '\\p{Cc}\\p{Cf}\\p{Zl}\\p{Zp}';
export const SINGLE_LINE_TEXT = new RegExp(`^[^${LINE_BREAKING_CHAR_CLASS}]*$`, 'u');

/**
 * Theme code: `^[a-z][a-z0-9_]{0,39}$`, never `uncategorized` — the same rule
 * as the CHECK of `org_themes.code`. The server generates it; a client never
 * sends one.
 */
export const orgThemeCodeSchema = z
  .string()
  .regex(THEME_CODE_PATTERN)
  .refine((code) => code !== RESERVED_THEME_CODE, 'uncategorized is reserved');

/**
 * A name the organization types: trimmed, 1..60, single line, and with a
 * dedup key (`normalizeThemeKey`) — a name of punctuation alone has none.
 */
export const orgThemeNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(ORG_THEME_NAME_MAX)
  .regex(SINGLE_LINE_TEXT, 'must be a single line of text')
  .refine((name) => normalizeThemeKey(name).length > 0, 'must contain a letter or digit');

/** What the theme covers, ≤ 80, single line; empty is allowed. */
export const orgThemeDefinitionSchema = z
  .string()
  .trim()
  .max(ORG_THEME_DEFINITION_MAX)
  .regex(SINGLE_LINE_TEXT, 'must be a single line of text');

/**
 * Request bodies are NOT `.strict()`: an unknown field is dropped, not
 * refused. A field a later UI adds must not turn into a 400 on every call
 * while edge-api is rolled back (it ships first, and is rolled back alone) —
 * and the one field a client must not choose, `code`, is not in the schema,
 * so dropping it is exactly the refusal it needs.
 */
export const createOrgThemeRequestSchema = z.object({
  name: orgThemeNameSchema,
  definition: orgThemeDefinitionSchema.default(''),
});

/**
 * PATCH: rename (keeps the history — same code, same row), change the
 * definition (bumps `definitionVersion`), or both. At least one; the client
 * sends only the fields it changed.
 *
 * `expectedUpdatedAt` — the theme's `updatedAt` as the client loaded it, the
 * precondition of the edit: the theme changed since (another admin's rename
 * or definition) answers 409 `theme_changed` instead of a stale form
 * silently reverting it. `updatedAt` moves on every write to the theme.
 */
export const updateOrgThemeRequestSchema = z
  .object({
    name: orgThemeNameSchema.optional(),
    definition: orgThemeDefinitionSchema.optional(),
    expectedUpdatedAt: z.string().min(1).max(40),
  })
  .refine(
    (body) => body.name !== undefined || body.definition !== undefined,
    'name or definition is required'
  );

/** `:id` of a theme route — `org_themes.id`, a UUID the server generated. */
export const orgThemeIdParamSchema = z.object({ id: z.string().min(1).max(64) });

export const listOrgThemesQuerySchema = z.object({
  /** Only themes in this status; absent — every status, the archive included. */
  status: orgThemeStatusSchema.optional(),
  /**
   * `no` — the screen declines the demo of a closed section (#2816): the route
   * then refuses below the plan instead of answering the demo set. Absent — the
   * demo opening may be answered. Anything but `yes`/`no` is a 400.
   */
  includeDemo: z.enum(['yes', 'no']).optional(),
});

export const orgThemeSchema = z.object({
  id: z.string().min(1),
  code: orgThemeCodeSchema,
  name: z.string().min(1),
  definition: z.string(),
  definitionVersion: z.number().int().min(1),
  status: orgThemeStatusSchema,
  createdAt: z.string(),
  /** First activation; `null` only while `reserved`. Unarchiving keeps it. */
  activatedAt: z.string().nullable(),
  updatedAt: z.string(),
});

export const ORG_THEME_OVERLAP_DIMENSIONS = [
  'call_category',
  'question_subject',
] as const satisfies readonly ThemeOverlapDimension[];

/**
 * «Тема ≠ категория» (spec §2.1): the typed name repeats a call category or a
 * question subject. A warning on manual input, never a refusal — the theme is
 * saved; the screen names what it repeats.
 */
export const orgThemeOverlapSchema = z.object({
  dimension: z.enum(ORG_THEME_OVERLAP_DIMENSIONS),
  code: z.string().min(1),
});

export const listOrgThemesResponseSchema = z.object({
  themes: z.array(orgThemeSchema),
  demoOnly: lockedDemoOnlySchema,
  /** `active` + `reserved` now — the cap's count, archive excluded. */
  counted: z.number().int().min(0),
  cap: z.number().int().min(1),
  /**
   * Uspacy portals of the organization whose accepted processing instruction
   * does not name conversation themes (#1608): their calls get no theme and do
   * not feed the suggestions until the current instruction is accepted in
   * Settings. Optional: an edge-api a release behind does not send it.
   */
  uspacyExcludedPortals: z.array(z.string().min(1)).optional(),
});

/** Create, rename / definition, archive, unarchive — the theme as written. */
export const orgThemeMutationResponseSchema = z.object({
  theme: orgThemeSchema,
  /** Present on create and rename: what the name repeats (§2.1); empty — nothing. */
  overlaps: z.array(orgThemeOverlapSchema).optional(),
});

/**
 * Refusal codes of the theme-set routes (body `{ error, code }`):
 * - `theme_set_full` 409 — 30 themes in `active` + `reserved`: archive one;
 * - `theme_duplicate` 409 — the name's key is taken, the body carries
 *   `theme` (archived included — the screen offers to restore it);
 * - `theme_not_found` 404; `theme_changed` 409 — edited meanwhile, reload;
 * - `theme_status_conflict` 409 — archive of a non-active theme, unarchive of
 *   a non-archived one;
 * - `tier_not_eligible` / `feature_disabled` 403 — the section's gate
 *   (Voice of Customer itself);
 * - `themes_disabled` 403 — the section is open, the theme set's own flag
 *   (`enableConversationThemes`, off for this organization) is not: a different
 *   thing to tell the customer than «Voice of Customer is unavailable»;
 * - `theme_invalid` 400 — the body failed the contract; `fields` names which
 *   of `name` / `definition` (the screen says which and why, not «try again»).
 */
export const ORG_THEME_ERROR_CODES = [
  'theme_set_full',
  'theme_duplicate',
  'theme_not_found',
  'theme_changed',
  'theme_status_conflict',
  'tier_not_eligible',
  'feature_disabled',
  'themes_disabled',
  'theme_invalid',
] as const;

/** The fields a theme body carries — what a `theme_invalid` 400 names. */
export const ORG_THEME_FIELDS = ['name', 'definition'] as const;

export const orgThemeErrorResponseSchema = z.object({
  error: z.string(),
  code: z.enum(ORG_THEME_ERROR_CODES),
  theme: orgThemeSchema.optional(),
  cap: z.number().int().optional(),
  fields: z.array(z.enum(ORG_THEME_FIELDS)).optional(),
});

export type OrgTheme = z.infer<typeof orgThemeSchema>;
export type OrgThemeOverlap = z.infer<typeof orgThemeOverlapSchema>;
export type CreateOrgThemeRequest = z.input<typeof createOrgThemeRequestSchema>;
export type UpdateOrgThemeRequest = z.infer<typeof updateOrgThemeRequestSchema>;
export type ListOrgThemesQuery = z.infer<typeof listOrgThemesQuerySchema>;
export type ListOrgThemesResponse = z.infer<typeof listOrgThemesResponseSchema>;
export type OrgThemeMutationResponse = z.infer<typeof orgThemeMutationResponseSchema>;
export type OrgThemeErrorCode = (typeof ORG_THEME_ERROR_CODES)[number];
export type OrgThemeErrorResponse = z.infer<typeof orgThemeErrorResponseSchema>;
